/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Form,
  Input,
  Select,
  Table,
  Tooltip,
  notification,
} from 'antd';
import { Button as CrceButton } from '@crc-evans/ui-components';
import {
  Beaker,
  Layers,
  Pencil,
  Plus,
  Search,
  ToggleLeft,
  ToggleRight,
  X,
} from 'lucide-react';
import axiosConfig from '../../services/axiosConfig';
import './ConsumableMaterialAdmin.css';

const { Option } = Select;

const MATERIAL_TYPES = ['coating', 'welding'];

/* ─────────────────────────────────────────────────────────────────────────────
   Generic master admin page – used for both Consumable and Material
───────────────────────────────────────────────────────────────────────────── */
const MasterAdmin = ({ endpoint, title }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [form] = Form.useForm();

  /* ── Fetch list ── */
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosConfig.get(`/${endpoint}/all`);
      const items = res?.data?.data ?? res?.data ?? [];
      const list = Array.isArray(items) ? items : [];
      setData(list.map((item, i) => ({ ...item, sno: i + 1 })));
    } catch {
      notification.error({ message: `Failed to load ${title} list` });
    } finally {
      setLoading(false);
    }
  }, [endpoint, title]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    setPage(1);
  }, [searchText]);

  /* ── PUT { status } ── */
  const handleStatusChange = useCallback(
    async (record, nextStatus) => {
      if (!record?.id) return;
      setStatusUpdatingId(record.id);
      try {
        await axiosConfig.put(`/${endpoint}/${record.id}`, { status: nextStatus });
        notification.success({
          message: `${title} marked as ${nextStatus === 'active' ? 'Active' : 'Inactive'}`,
        });
        await fetchData();
      } catch {
        notification.error({ message: `Failed to update ${title} status` });
      } finally {
        setStatusUpdatingId(null);
      }
    },
    [endpoint, title, fetchData]
  );

  /* ── Open Add ── */
  const handleAdd = useCallback(() => {
    setEditRecord(null);
    form.resetFields();
    form.setFieldsValue({ status: 'active' });
    setModalVisible(true);
  }, [form]);

  /* ── Open Edit ── */
  const handleEdit = useCallback(
    (record) => {
      setEditRecord(record);
      form.setFieldsValue({
        category: record.category ?? '',
        name: record.name ?? '',
        productCode: record.productCode ?? '',
        unit: record.unit ?? '',
        materialType: record.materialType ?? undefined,
        status: (record.status || 'active').toLowerCase() === 'inactive' ? 'inactive' : 'active',
      });
      setModalVisible(true);
    },
    [form]
  );

  /* ── Save (create or update) ── */
  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      if (editRecord) {
        await axiosConfig.put(`/${endpoint}/${editRecord.id}`, values);
        notification.success({ message: `${title} updated successfully` });
      } else {
        await axiosConfig.post(`/${endpoint}`, values);
        notification.success({ message: `${title} added successfully` });
      }
      setModalVisible(false);
      form.resetFields();
      setEditRecord(null);
      await fetchData();
    } catch (err) {
      if (err?.errorFields) return;
      notification.error({ message: `Failed to save ${title}` });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setModalVisible(false);
    form.resetFields();
    setEditRecord(null);
  };

  /* ── Filtered data ── */
  const filteredData = useMemo(() => {
    if (!searchText.trim()) return data;
    const term = searchText.toLowerCase();
    return data.filter(
      (item) =>
        (item.category || '').toLowerCase().includes(term) ||
        (item.name || '').toLowerCase().includes(term) ||
        (item.productCode || '').toLowerCase().includes(term) ||
        (item.unit || '').toLowerCase().includes(term) ||
        (item.materialType || '').toLowerCase().includes(term) ||
        (item.status || '').toLowerCase().includes(term)
    );
  }, [data, searchText]);

  useEffect(() => {
    const lastPage = Math.max(1, Math.ceil(filteredData.length / pageSize) || 1);
    setPage((p) => (p > lastPage ? lastPage : p));
  }, [filteredData.length, pageSize]);

  /* ── Ant Design Table columns ── */
  const columns = useMemo(
    () => [
      {
        title: 'S.No',
        dataIndex: 'sno',
        key: 'sno',
        width: 70,
        align: 'center',
        render: (_, __, index) => index + 1,
      },
      {
        title: 'Category',
        dataIndex: 'category',
        key: 'category',
        width: 160,
        sorter: (a, b) => (a.category || '').localeCompare(b.category || ''),
      },
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        ellipsis: true,
        sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
      },
      {
        title: 'Product Code',
        dataIndex: 'productCode',
        key: 'productCode',
        width: 180,
        sorter: (a, b) => (a.productCode || '').localeCompare(b.productCode || ''),
      },
      {
        title: 'Unit',
        dataIndex: 'unit',
        key: 'unit',
        width: 120,
        render: (val) => val || '—',
      },
      {
        title: 'Material Type',
        dataIndex: 'materialType',
        key: 'materialType',
        width: 140,
        filters: MATERIAL_TYPES.map((t) => ({
          text: t.charAt(0).toUpperCase() + t.slice(1),
          value: t,
        })),
        onFilter: (value, record) =>
          (record.materialType || '').toLowerCase() === value.toLowerCase(),
        render: (val) => (val ? val.charAt(0).toUpperCase() + val.slice(1) : '—'),
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: 120,
        align: 'center',
        filters: [
          { text: 'Active', value: 'active' },
          { text: 'Inactive', value: 'inactive' },
        ],
        onFilter: (value, record) =>
          (record.status || 'active').toLowerCase() === value,
        render: (val) => {
          const isActive = (val || 'active').toLowerCase() === 'active';
          return (
            <span
              className={`cm-status-badge ${
                isActive ? 'cm-status-badge--active' : 'cm-status-badge--inactive'
              }`}
            >
              <span
                className={`cm-status-dot ${
                  isActive ? 'cm-status-dot--active' : 'cm-status-dot--inactive'
                }`}
              />
              {isActive ? 'Active' : 'Inactive'}
            </span>
          );
        },
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 100,
        align: 'center',
        fixed: 'right',
        render: (_, record) => {
          const isActive = (record.status || 'active').toLowerCase() === 'active';
          const isUpdating = statusUpdatingId === record.id;
          return (
            <div className="cm-actions-cell">
              <Tooltip title="Edit">
                <button
                  type="button"
                  className="cm-action-btn cm-action-edit"
                  onClick={() => handleEdit(record)}
                >
                  <Pencil size={16} strokeWidth={2} />
                </button>
              </Tooltip>
              <Tooltip title={isActive ? 'Make Inactive' : 'Make Active'}>
                <button
                  type="button"
                  className="cm-action-btn cm-action-toggle"
                  disabled={isUpdating}
                  onClick={() =>
                    handleStatusChange(record, isActive ? 'inactive' : 'active')
                  }
                >
                  {isActive ? (
                    <ToggleRight size={18} strokeWidth={2} />
                  ) : (
                    <ToggleLeft size={18} strokeWidth={2} />
                  )}
                </button>
              </Tooltip>
            </div>
          );
        },
      },
    ],
    [handleEdit, handleStatusChange, statusUpdatingId]
  );

  const headerIcon = title === 'Material' ? Layers : Beaker;
  const HeaderIcon = headerIcon;

  return (
    <div className="modern-cm-page">
      {/* ── Header ── */}
      <div className="cm-header">
        <div className="cm-header-left">
          <div className="cm-header-icon">
            <HeaderIcon size={22} color="#3b82f6" strokeWidth={2} />
          </div>
          <div className="cm-header-text">
            <h2 className="cm-title">{title} Master</h2>
            <p className="cm-subtitle">Manage {title.toLowerCase()} items, categories and status</p>
          </div>
        </div>
        <div className="cm-header-right">
          <CrceButton
            label={`Add ${title}`}
            variant="primary"
            size="medium"
            icon={Plus}
            onClick={handleAdd}
          />
        </div>
      </div>

      {/* ── Search Toolbar ── */}
      <div className="cm-toolbar">
        <div className="cm-search-wrapper">
          <Search className="cm-search-icon" size={16} strokeWidth={2} />
          <input
            className="cm-search-input"
            type="text"
            placeholder={`Search ${title.toLowerCase()}s...`}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </div>
      </div>

      {/* ── Table (wrap + pagination class match other admin / heat-profile lists) ── */}
      <div className="cm-table-wrap heat-profile-table-wrap">
        <Table
          className="heat-profile-table h-100 pipeline-table equipPaginationTable"
          rowKey={(r) => r.id ?? r.sno}
          columns={columns}
          dataSource={filteredData}
          loading={loading}
          size="middle"
          scroll={{ x: 1000 }}
          pagination={
            filteredData.length > 0
              ? {
                current: page,
                pageSize,
                total: filteredData.length,
                position: ['bottomRight'],
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50', '100'],
                className: 'heat-profile-pagination',
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                onChange: (p, size) => {
                  setPage(p);
                  if (size && size !== pageSize) setPageSize(size);
                },
                onShowSizeChange: (current, size) => {
                  setPage(1);
                  setPageSize(size);
                },
              }
              : false
          }
        />
      </div>

      {/* ── Add / Edit Modal ── */}
      {modalVisible && (
        <div className="cm-modal-backdrop">
          <div
            className="cm-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="cm-modal-head">
              <h3 className="cm-modal-title">
                {editRecord ? `Edit ${title}` : `Add ${title}`}
              </h3>
              <button
                type="button"
                className="cm-modal-close"
                onClick={handleCancel}
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>
            <div className="cm-modal-body">
              <Form form={form} layout="vertical" autoComplete="off" name={`${endpoint}-form`}>
                <div className="cm-modal-form-grid">
                  <div className="cm-modal-form-row">
                    <div className="cm-modal-field">
                      <label>Category <span className="required">*</span></label>
                      <Form.Item
                        name="category"
                        rules={[{ required: true, message: 'Category is required' }]}
                        noStyle
                      >
                        <Input className="cm-modal-input" placeholder="Enter category" />
                      </Form.Item>
                    </div>
                  </div>
                  <div className="cm-modal-form-row">
                    <div className="cm-modal-field">
                      <label>Name <span className="required">*</span></label>
                      <Form.Item
                        name="name"
                        rules={[{ required: true, message: 'Name is required' }]}
                        noStyle
                      >
                        <Input className="cm-modal-input" placeholder="Enter name" />
                      </Form.Item>
                    </div>
                  </div>
                  <div className="cm-modal-form-row">
                    <div className="cm-modal-field">
                      <label>Product Code <span className="required">*</span></label>
                      <Form.Item
                        name="productCode"
                        rules={[{ required: true, message: 'Product code is required' }]}
                        noStyle
                      >
                        <Input className="cm-modal-input" placeholder="Enter product code" />
                      </Form.Item>
                    </div>
                  </div>
                  <div className="cm-modal-form-row">
                    <div className="cm-modal-field">
                      <label>Unit</label>
                      <Form.Item name="unit" noStyle>
                        <Input className="cm-modal-input" placeholder="e.g. Kg, Bag (25Kg)" />
                      </Form.Item>
                    </div>
                  </div>
                  <div className="cm-modal-form-row">
                    <div className="cm-modal-field">
                      <label>Material Type <span className="required">*</span></label>
                      <Form.Item
                        name="materialType"
                        rules={[{ required: true, message: 'Material type is required' }]}
                        noStyle
                      >
                        <Select
                          className="cm-modal-select"
                          placeholder="Select material type"
                          dropdownClassName="cm-modal-select-dropdown"
                          getPopupContainer={(trigger) => trigger.parentNode}
                        >
                          {MATERIAL_TYPES.map((t) => (
                            <Option key={t} value={t}>
                              {t.charAt(0).toUpperCase() + t.slice(1)}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </div>
                  </div>
                  <div className="cm-modal-form-row">
                    <div className="cm-modal-field">
                      <label>Status <span className="required">*</span></label>
                      <Form.Item
                        name="status"
                        rules={[{ required: true, message: 'Status is required' }]}
                        noStyle
                      >
                        <Select
                          className="cm-modal-select"
                          placeholder="Select status"
                          dropdownClassName="cm-modal-select-dropdown"
                          getPopupContainer={(trigger) => trigger.parentNode}
                        >
                          <Option value="active">Active</Option>
                          <Option value="inactive">Inactive</Option>
                        </Select>
                      </Form.Item>
                    </div>
                  </div>
                </div>
              </Form>
            </div>
            <div className="cm-modal-foot">
              <CrceButton label="Cancel" variant="tertiary" size="medium" onClick={handleCancel} />
              <CrceButton
                label={saving ? 'Saving...' : editRecord ? 'Update' : 'Add'}
                variant="primary"
                size="medium"
                onClick={handleSave}
                disabled={saving}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────────────────
   Named exports – each used as a separate top-level admin tab
───────────────────────────────────────────────────────────────────────────── */
export const ConsumableMasterAdmin = () => (
  <MasterAdmin endpoint="consumable-master" title="Consumable" />
);

export const MaterialMasterAdmin = () => (
  <MasterAdmin endpoint="material-master" title="Material" />
);
